/**
 * CaseSummaryHeader — compact status overview at the top of the investigation timeline.
 *
 * Shows badge counts per status category and a one-line assessment message.
 */

import { Shield } from 'lucide-react';
import type { InvestigationSummary } from '../../types/orderInvestigation';
import { PROVENANCE_SOFT_BADGE_CLASSES, MILESTONE_STATUS_SOFT_BADGE_CLASSES } from '../../config/colors';

interface CaseSummaryHeaderProps {
  summary: InvestigationSummary;
}

// Tier badge colours come from the shared colors module (ticket 15) —
// inferred is violet, never the amber it drifted to here. `missing` and
// `contradictory` are not provenance tiers; their badge expressions come
// from the shared non-tier map in the same module.
const STATUS_BADGES: { key: keyof Omit<InvestigationSummary, 'message' | 'overallCaseStatus'>; label: string; color: string }[] = [
  { key: 'observed',      label: 'Observed',      color: PROVENANCE_SOFT_BADGE_CLASSES.observed },
  { key: 'declared',      label: 'Declared',      color: PROVENANCE_SOFT_BADGE_CLASSES.declared },
  { key: 'inferred',      label: 'Inferred',      color: PROVENANCE_SOFT_BADGE_CLASSES.inferred },
  { key: 'missing',       label: 'Missing',       color: MILESTONE_STATUS_SOFT_BADGE_CLASSES.missing },
  { key: 'contradictory', label: 'Contradictory', color: MILESTONE_STATUS_SOFT_BADGE_CLASSES.contradictory },
];

export default function CaseSummaryHeader({ summary }: CaseSummaryHeaderProps) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4 border border-accent/30">
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-brand" />
        <span className="text-sm font-semibold text-foreground uppercase tracking-wide">
          Evidence Summary
        </span>
      </div>

      {/* Badge row */}
      <div className="flex flex-wrap gap-2 mb-3">
        {STATUS_BADGES.map(({ key, label, color }) => {
          const count = summary[key];
          if (count === 0) return null;
          return (
            <span
              key={key}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${color}`}
            >
              <span className="font-bold">{count}</span>
              {label}
            </span>
          );
        })}
      </div>

      {/* Assessment message */}
      <p className="text-sm text-muted-foreground leading-relaxed">{summary.message}</p>
    </div>
  );
}
